import { CREATOR_TAG_COLORS, type CreatorTagColor } from "@unbox/api";

/** Picks a random colour from the server-enforced palette for a newly created tag. */
export const randomTagColor = (): CreatorTagColor =>
  CREATOR_TAG_COLORS[Math.floor(Math.random() * CREATOR_TAG_COLORS.length)];

type Rgb = [number, number, number];

// Palette colours are always 6-digit hex (`#rrggbb`), enforced server-side.
const hexToRgb = (hex: string): Rgb => {
  const int = Number.parseInt(hex.replace("#", ""), 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

const rgbToHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

/** Linear blend from `a` toward `b` by `t` (0..1). */
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/** WCAG relative luminance. */
const luminance = ([r, g, b]: Rgb): number => {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastRatio = (a: number, b: number): number => {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
};

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/**
 * Text colour for a tag pill that stays legible against the pill's pale tint
 * background (`color-mix(... 20% ...)` of the tag colour over white). Returns the
 * tag colour itself when it already clears WCAG AA (4.5:1); otherwise darkens it
 * toward black in small steps until it does. Deterministic — the palette is fixed.
 */
export const readableTextColor = (hex: string, tintRatio = 0.2, target = 4.5): string => {
  const color = hexToRgb(hex);
  const backgroundLuminance = luminance(mix(WHITE, color, tintRatio));

  for (let t = 0; t <= 1; t += 0.1) {
    const candidate = mix(color, BLACK, t);
    if (contrastRatio(luminance(candidate), backgroundLuminance) >= target) {
      return rgbToHex(candidate);
    }
  }
  return rgbToHex(BLACK);
};
